import difference from 'lodash/difference'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { push } from 'react-router-redux'

import { CLAN_MANAGEMENT, ROOT } from 'urls'
import clanManagementSettings from 'containers/ClanManagement/settings.js'
import { clanManagementOperation } from 'containers/ClanManagement/proxy'
import { isServerError } from 'containers/ClanManagement/proxy/helpers.js'
import { getAssignableRole } from 'utils/clan_management/roles.js'
import { resetClan } from 'state/actions/clan'
import {
    startClanManagementFetching,
    stopClanManagementFetching,
    clanManagementToggleClanInTransaction,
    fetchClanInfo,
} from './ClanManagement'

import {
    removeMemberSuccessNotification,
    removeMemberErrorNotification,
    changeRoleSuccessNotification,
    changeRoleErrorNotification,
    leavingFromClanSuccessNotification,
} from './Notifications/MemberManagement'
import { changeCommanderSuccessNotification, changeCommanderErrorNotification } from './Notifications/ChangeCommander'
import {
    resourceAllocationSuccessNotification,
    resourceAllocationErrorNotification,
} from './Notifications/ResourceAllocation'
import {
    CANNOT_CHANGE_ROLE,
    CANNOT_REMOVE_MEMBERS,
    CANNOT_ALLOCATE_RESOURCE,
    ROLE_ALREADY_ASSIGNED,
} from './Notifications/base'
import requestAccountInfo from 'state/actions/account'
import { sendErrorNotification } from 'react-wot-bootstrap'
import { formatError, formatCommonError } from '../recommended_clans'

export const UNTICK_CLAN_MEMBERS = 'UNTICK_CLAN_MEMBERS'
export const CLEAR_MEMBERS_OPERATION = 'CLEAR_MEMBERS_OPERATION'
export const SELECT_ROLE_MEMBERS_OPERATION = 'SELECT_ROLE_MEMBERS_OPERATION'
export const TOGGLE_ALL_MEMBERS_TICK = 'TOGGLE_ALL_MEMBERS_TICK'
export const TOGGLE_MEMBER_TICK = 'TOGGLE_MEMBER_TICK'
export const RESET_SELECTED_MEMBERS = 'RESET_SELECTED_MEMBERS'
export const REMOVE_MEMBERS_SUCCESS = 'REMOVE_MEMBERS_SUCCESS'
export const DIVIDE_PROFITS_SUCCESS = 'DIVIDE_PROFITS_SUCCESS'
export const CHANGE_ROLE_SUCCESS = 'CHANGE_ROLE_SUCCESS'
export const UPDATE_MEMBERS = 'UPDATE_MEMBERS'
export const SORT_MEMBERS = 'SORT_MEMBERS'

const EXCLUDED_FROM_CLAN_ERROR_CODE = 29

export const toggleMemberTick = (memberId) => ({
    type: TOGGLE_MEMBER_TICK,
    memberId,
})

export const toggleAllMembersTick = (memberIds) => ({
    type: TOGGLE_ALL_MEMBERS_TICK,
    memberIds,
})

export const clearMembersOperation = () => ({
    type: CLEAR_MEMBERS_OPERATION,
})

export const selectRoleMembersOperation = (roleName) => ({
    type: SELECT_ROLE_MEMBERS_OPERATION,
    roleName,
})

export const untickClanMembers = (memberIds) => ({
    type: UNTICK_CLAN_MEMBERS,
    memberIds,
})

export const resetSelectedMembers = () => ({
    type: RESET_SELECTED_MEMBERS,
})

export const removeMembersSuccess = (memberIds) => ({
    type: REMOVE_MEMBERS_SUCCESS,
    memberIds,
})

export const sortMembers = (column) => ({
    type: SORT_MEMBERS,
    column,
})

export const allocatingResourcesSuccess = (delta, currency) => ({
    type: DIVIDE_PROFITS_SUCCESS,
    delta,
    currency,
})

export const changeRoleSuccess = (memberIds) => ({
    type: CHANGE_ROLE_SUCCESS,
    memberIds,
})

export const updateMembers = (members) => ({
    type: UPDATE_MEMBERS,
    members,
})

const fetchWrapperWithLocalSpinnerAndToggleClanInTransaction = (dispatch, promise) => {
    dispatch(clanManagementToggleClanInTransaction())
    dispatch(startClanManagementFetching())
    promise
        .then(() => {
            dispatch(stopClanManagementFetching())
            dispatch(clanManagementToggleClanInTransaction())
        })
        .catch(() => {
            dispatch(stopClanManagementFetching())
            dispatch(clanManagementToggleClanInTransaction())
        })
    return promise
}

export const removeMembers = (memberId) => {
    return (dispatch, getState) => {
        const { clan, membersOperation, settings, members, account } = getState()
        const clanId = clan.id
        const data = {
            account_ids: memberId ? [memberId] : membersOperation.selectedIds,
        }

        const management = clanManagementSettings.urls.find((item) => item.key === 'remove_member')
        const options = {
            host: settings.gatewayHost,
            data,
            clanId,
        }

        const promise = clanManagementOperation(management, options)
            .then((json) => {
                const playersRemoved = members.members.filter((member) => json.accountIds.includes(member.id))
                const playersName = playersRemoved.map((member) => member.name)
                dispatch(removeMembersSuccess(json.accountIds))
                dispatch(removeMemberSuccessNotification(playersName))

                if (json && json.additionalData) {
                    const error = {
                        code: CANNOT_REMOVE_MEMBERS,
                        additional_data: json.additionalData,
                    }
                    dispatch(removeMemberErrorNotification(error, account.id, members.members))
                }
                dispatch(fetchClanInfo())
            })
            .catch((e) => {
                if (isServerError(e)) {
                    dispatch(fetchClanInfo())
                }
                dispatch(removeMemberErrorNotification(e.response, account.id, members.members))
            })
        return fetchWrapperWithLocalSpinnerAndToggleClanInTransaction(dispatch, promise)
    }
}

const createNewMember = (member, role) => ({
    ...member,
    role,
})

export const changeCommander = (memberId) => {
    return (dispatch, getState) => {
        const { membersOperation, clan, account, members, settings } = getState()
        if (!memberId && membersOperation.selectedIds.length !== 1) {
            throw new Error('Unexpected behaviour: selected more than one member')
        }

        const newCommanderId = memberId || membersOperation.selectedIds[0]
        const clanId = clan.id
        const accountId = account.id
        const defaultRoleAfterCommander = 'executive_officer'
        const newMembers = members.members.map((member) => {
            if (member.id === newCommanderId) {
                return createNewMember(member, 'commander')
            } else if (member.id === accountId) {
                return createNewMember(member, defaultRoleAfterCommander)
            }
            return {
                ...member,
            }
        })

        const data = {
            ids: [newCommanderId],
            role: 'commander',
        }
        const management = clanManagementSettings.urls.find((item) => item.key === 'change_commander')
        const options = {
            host: settings.gatewayHost,
            data,
            clanId,
        }

        const promise = clanManagementOperation(management, options)
            .then(() => {
                dispatch(changeRoleSuccess([newCommanderId, accountId]))
                dispatch(updateMembers(newMembers))
                const newCommanderMember = newMembers.find((member) => member.id === newCommanderId)
                dispatch(changeCommanderSuccessNotification(newCommanderMember.name, defaultRoleAfterCommander))
                dispatch(fetchClanInfo())
            })
            .catch((e) => {
                if (isServerError(e)) {
                    dispatch(fetchClanInfo())
                }
                dispatch(changeCommanderErrorNotification(e.response, account.id, members.members))
            })
        return fetchWrapperWithLocalSpinnerAndToggleClanInTransaction(dispatch, promise)
    }
}

export const allocateResources = (amountToEach, currency, memberId) => {
    return (dispatch, getState) => {
        const { membersOperation, clan, settings, members, account } = getState()
        const selectedIds = memberId ? [memberId] : membersOperation.selectedIds
        if (isEmpty(selectedIds)) {
            throw new Error('Unexpected behaviour: not selected members')
        }

        const data = {
            receivers: selectedIds,
            delta: amountToEach,
            currency: currency,
            // An unique classifier for separate treasury operation for WGCP project.
            classifier: 2,
        }

        const clanId = clan.id
        const management = clanManagementSettings.urls.find((item) => item.key === 'divide_profits')
        const options = {
            host: settings.gatewayHost,
            data,
            clanId,
        }
        const promise = clanManagementOperation(management, options)
            .then((json) => {
                const hasErrorData = json && json.additionalData
                const validMembersIds = hasErrorData ? json.additionalData.valid_ids : selectedIds
                const totalDivided = validMembersIds.length * amountToEach
                const playersReceived = members.members.filter((member) => validMembersIds.includes(member.id))
                const playersName = playersReceived.map((member) => member.name)

                dispatch(allocatingResourcesSuccess(totalDivided, currency))
                dispatch(resourceAllocationSuccessNotification(amountToEach, currency, playersName))
                if (hasErrorData) {
                    const error = {
                        code: CANNOT_ALLOCATE_RESOURCE,
                        additional_data: json.additionalData,
                    }
                    dispatch(resourceAllocationErrorNotification(error, account.id, members.members, currency))
                }
                dispatch(fetchClanInfo())
                dispatch(requestAccountInfo())
            })
            .catch((e) => {
                if (isServerError(e)) {
                    dispatch(fetchClanInfo())
                }
                dispatch(resourceAllocationErrorNotification(e.response, account.id, members.members, currency))
                dispatch(requestAccountInfo())
            })
        return fetchWrapperWithLocalSpinnerAndToggleClanInTransaction(dispatch, promise)
    }
}

const getSuccessfullyChangedIds = (selectedIds, json) =>
    difference(
        selectedIds,
        get(json, 'additionalData.reason_not_in_clan_ids'),
        Object.keys(get(json, 'additionalData.reason_rank_too_high_accounts', {})).map((id) => +id)
    )

export const changeRole = (memberId) => {
    return (dispatch, getState) => {
        const { clan, membersOperation, account, members, settings } = getState()
        const clanId = clan.id

        const currentMember = members.members.find((member) => member.id === account.id)
        const roleName = getAssignableRole(currentMember.role, membersOperation.selectedRole)
        const selectedIds = memberId ? [memberId] : membersOperation.selectedIds
        const data = {
            ids: selectedIds,
            role: roleName,
        }

        const management = clanManagementSettings.urls.find((item) => item.key === 'change_role')
        const options = {
            host: settings.gatewayHost,
            data,
            clanId,
        }

        const newMembers = members.members.map((member) => {
            if (selectedIds.includes(member.id)) {
                return createNewMember(member, roleName)
            }
            return {
                ...member,
            }
        })

        const promise = clanManagementOperation(management, options)
            .catch((json) => {
                if (json.response && json.response.code === ROLE_ALREADY_ASSIGNED) {
                    return {
                        accountIds: selectedIds,
                        clanId,
                    }
                }

                return Promise.reject(json)
            })
            .then((json) => {
                const accountIds = getSuccessfullyChangedIds(selectedIds, json)
                dispatch(changeRoleSuccess(accountIds))
                dispatch(updateMembers(newMembers))
                dispatch(selectRoleMembersOperation(null))
                const playersChangedRole = newMembers.filter((member) => accountIds.includes(member.id))
                const playersName = playersChangedRole.map((member) => member.name)
                dispatch(changeRoleSuccessNotification(roleName, playersName))

                if (json && json.additionalData) {
                    const error = {
                        code: CANNOT_CHANGE_ROLE,
                        additional_data: json.additionalData,
                    }
                    dispatch(changeRoleErrorNotification(error, account.id, members.members))
                }
                dispatch(fetchClanInfo())
            })
            .catch((e) => {
                if (isServerError(e)) {
                    dispatch(selectRoleMembersOperation(null))
                    dispatch(fetchClanInfo())
                }
                dispatch(changeRoleErrorNotification(e.response, account.id, members.members))
            })

        return fetchWrapperWithLocalSpinnerAndToggleClanInTransaction(dispatch, promise)
    }
}

export const leaveClan = (reasons) => {
    return (dispatch, getState) => {
        const { clan, account, settings } = getState()
        const clanId = clan.id
        const memberId = account.id
        const data = {
            account_ids: [memberId],
            leave_reasons: reasons || undefined,
        }

        const management = clanManagementSettings.urls.find((item) => item.key === 'remove_member')
        const options = {
            host: settings.gatewayHost,
            data,
            clanId,
        }

        const promise = clanManagementOperation(management, options)
            .then(() => {
                dispatch(resetClan())
                dispatch(requestAccountInfo())
                dispatch(leavingFromClanSuccessNotification())
                dispatch(push(ROOT))
            })
            .catch((errorData) => {
                const errorCode = get(errorData, 'response.code', 1)
                const errorMessage = formatError(errorCode) || formatCommonError()

                sendErrorNotification(errorMessage)
                if (errorCode === EXCLUDED_FROM_CLAN_ERROR_CODE) {
                    dispatch(resetClan())
                } else {
                    dispatch(push(CLAN_MANAGEMENT))
                }
            })
        return fetchWrapperWithLocalSpinnerAndToggleClanInTransaction(dispatch, promise)
    }
}



// WEBPACK FOOTER //
// ./state/actions/CMT/MembersOperation.js